import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { ChevronLeft, Settings, Save, Phone, PhoneIncoming, Bot, Clock, Languages } from 'lucide-react';
import useApiMethods from '../../../lib/useApiMethods';
import { cn } from '../../../utils/tailwindUtils';
import ToastMsg from '../../../components/common/ToastMsg';
import { ENGAGE_AI_CALLER, LANGUAGE_LABEL } from './aiCallerConstants';


export default function EngageAICallerSettings() {
  const navigate = useNavigate();
  const { fetch: apiFetch, update: apiUpdate } = useApiMethods({ throwOnFail: true });

  const [config, setConfig] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    (async () => {
      try {
        const resp = await apiFetch(ENGAGE_AI_CALLER.CONFIG);
        const data = resp?.data || resp?.data?.data || null;
        setConfig(data || {
          agent_name: 'Priya', company_name: '', twilio_phone_number: '',
          inbound_enabled: false, inbound_lead_target: 'engage',
          inbound_greeting_template: '',
          voice_id: '', default_language: 'en', supported_languages: ['en'],
          system_prompt: '', knowledge_base: '', greeting_template: '',
          max_call_duration_seconds: 300, max_retries: 2, retry_delay_minutes: 60,
          calling_hours_start: '09:00', calling_hours_end: '18:00', is_enabled: false,
        });
      } catch (e) {
        setError(e?.response?.data?.message || 'Failed to load config');
      } finally {
        setLoading(false);
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const save = async () => {
    setSaving(true);
    try {
      const resp = await apiUpdate(ENGAGE_AI_CALLER.CONFIG, config);
      const data = resp?.data || resp?.data?.data || config;
      setConfig(data);
      ToastMsg('success', 'Settings saved');
    } catch (e) {
      ToastMsg('error', e?.response?.data?.message || 'Save failed');
    } finally {
      setSaving(false);
    }
  };

  const set = (k, v) => setConfig(c => ({ ...c, [k]: v }));

  const toggleLang = (code) => {
    const list = config.supported_languages || [];
    set('supported_languages', list.includes(code) ? list.filter(x => x !== code) : [...list, code]);
  };

  if (loading) return <div className="p-8 text-center text-gray-500 dark:text-gray-400">Loading…</div>;
  if (!config)  return <div className="p-4">{error || 'No config found'}</div>;

  return (
    <div className="p-4 max-w-[900px] mx-auto space-y-4">
      <button onClick={() => navigate('/engage/ai-caller/dashboard')} className="inline-flex items-center gap-1.5 px-2.5 py-1.5 rounded-lg text-sm text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-800">
        <ChevronLeft className="w-4 h-4" /> Back to AI Voice Agent
      </button>

      <div className="flex items-center justify-between gap-3">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-slate-500 to-gray-600 flex items-center justify-center text-white shadow-sm">
            <Settings className="w-5 h-5" />
          </div>
          <div>
            <h1 className="text-xl font-bold text-gray-900 dark:text-white">AI Voice Agent Settings</h1>
            <p className="text-xs text-gray-500 dark:text-gray-400 mt-0.5">Configure the voice, prompt, and call rules</p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <label className="inline-flex items-center gap-2 text-sm">
            <input type="checkbox" checked={!!config.is_enabled} onChange={e => set('is_enabled', e.target.checked)} />
            <span className={cn('font-medium', config.is_enabled ? 'text-green-600 dark:text-green-400' : 'text-gray-500')}>
              {config.is_enabled ? 'Enabled' : 'Disabled'}
            </span>
          </label>
          <button onClick={save} disabled={saving} className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-sm text-white bg-gradient-to-r from-teal-500 to-cyan-600 disabled:opacity-50">
            <Save className="w-3.5 h-3.5" /> {saving ? 'Saving…' : 'Save settings'}
          </button>
        </div>
      </div>

      {/* Agent identity */}
      <div className="bg-white dark:bg-gray-800 rounded-xl p-4 border border-gray-100 dark:border-gray-700">
        <h2 className="text-sm font-semibold text-gray-900 dark:text-white mb-3 flex items-center gap-1.5">
          <Bot className="w-4 h-4 text-teal-500" /> Agent identity
        </h2>
        <div className="grid grid-cols-2 gap-3">
          <Input label="Agent name" value={config.agent_name}     onChange={v => set('agent_name', v)} />
          <Input label="Hospital name" value={config.company_name} onChange={v => set('company_name', v)} />
          <Input label="Twilio phone number" value={config.twilio_phone_number} onChange={v => set('twilio_phone_number', v)} icon={Phone} />
          <Input label="ElevenLabs voice ID" value={config.voice_id} onChange={v => set('voice_id', v)} />
        </div>
      </div>

      {/* Languages */}
      <div className="bg-white dark:bg-gray-800 rounded-xl p-4 border border-gray-100 dark:border-gray-700">
        <h2 className="text-sm font-semibold text-gray-900 dark:text-white mb-3 flex items-center gap-1.5">
          <Languages className="w-4 h-4 text-purple-500" /> Languages
        </h2>
        <div className="grid grid-cols-2 gap-3 mb-3">
          <div>
            <label className="text-[11px] uppercase text-gray-500 font-medium">Default language</label>
            <select value={config.default_language} onChange={e => set('default_language', e.target.value)}
              className="mt-1 w-full px-3 py-2 text-sm rounded-lg bg-gray-50 dark:bg-gray-900/40 border border-gray-200 dark:border-gray-700 text-gray-900 dark:text-white">
              {Object.entries(LANGUAGE_LABEL).map(([v, l]) => <option key={v} value={v}>{l}</option>)}
            </select>
          </div>
        </div>
        <div>
          <label className="text-[11px] uppercase text-gray-500 font-medium">Supported languages</label>
          <div className="mt-1 flex flex-wrap gap-1.5">
            {Object.entries(LANGUAGE_LABEL).map(([v, l]) => (
              <button key={v} onClick={() => toggleLang(v)}
                className={cn('px-2.5 py-1 rounded-full text-xs',
                  (config.supported_languages || []).includes(v)
                    ? 'bg-purple-100 text-purple-700 dark:bg-purple-900/30 dark:text-purple-300'
                    : 'bg-gray-100 text-gray-700 dark:bg-gray-700 dark:text-gray-300')}>
                {l}
              </button>
            ))}
          </div>
        </div>
      </div>

      {/* Prompt */}
      <div className="bg-white dark:bg-gray-800 rounded-xl p-4 border border-gray-100 dark:border-gray-700">
        <h2 className="text-sm font-semibold text-gray-900 dark:text-white mb-3">Prompt & knowledge</h2>
        <div className="space-y-3">
          <div>
            <label className="text-[11px] uppercase text-gray-500 font-medium">Greeting template</label>
            <textarea value={config.greeting_template || ''} onChange={e => set('greeting_template', e.target.value)}
              placeholder="Use {customer_name}, {company_name}, {agent_name}"
              rows={2} className="mt-1 w-full px-3 py-2 text-sm rounded-lg bg-gray-50 dark:bg-gray-900/40 border border-gray-200 dark:border-gray-700 text-gray-900 dark:text-white" />
          </div>
          <div>
            <label className="text-[11px] uppercase text-gray-500 font-medium">System prompt</label>
            <textarea value={config.system_prompt || ''} onChange={e => set('system_prompt', e.target.value)}
              placeholder="Persona, tone, screening flow…"
              rows={6} className="mt-1 w-full px-3 py-2 text-sm rounded-lg bg-gray-50 dark:bg-gray-900/40 border border-gray-200 dark:border-gray-700 text-gray-900 dark:text-white" />
          </div>
          <div>
            <label className="text-[11px] uppercase text-gray-500 font-medium">Hospital knowledge base</label>
            <textarea value={config.knowledge_base || ''} onChange={e => set('knowledge_base', e.target.value)}
              placeholder="Specialties, doctors, timings, price ranges, insurance…"
              rows={6} className="mt-1 w-full px-3 py-2 text-sm rounded-lg bg-gray-50 dark:bg-gray-900/40 border border-gray-200 dark:border-gray-700 text-gray-900 dark:text-white" />
          </div>
        </div>
      </div>

      {/* Call rules */}
      <div className="bg-white dark:bg-gray-800 rounded-xl p-4 border border-gray-100 dark:border-gray-700">
        <h2 className="text-sm font-semibold text-gray-900 dark:text-white mb-3 flex items-center gap-1.5">
          <Clock className="w-4 h-4 text-amber-500" /> Call rules
        </h2>
        <div className="grid grid-cols-2 gap-3">
          <Input type="number" label="Max call duration (seconds)"
            value={config.max_call_duration_seconds} onChange={v => set('max_call_duration_seconds', Number(v) || 0)} />
          <Input type="number" label="Max retries"
            value={config.max_retries} onChange={v => set('max_retries', Number(v) || 0)} />
          <Input type="number" label="Retry delay (minutes)"
            value={config.retry_delay_minutes} onChange={v => set('retry_delay_minutes', Number(v) || 0)} />
          <div></div>
          <Input type="time" label="Calling hours start"
            value={config.calling_hours_start} onChange={v => set('calling_hours_start', v)} />
          <Input type="time" label="Calling hours end"
            value={config.calling_hours_end} onChange={v => set('calling_hours_end', v)} />
        </div>
      </div>

      {/* Inbound calls */}
      <div className="bg-white dark:bg-gray-800 rounded-xl p-4 border border-gray-100 dark:border-gray-700">
        <h2 className="text-sm font-semibold text-gray-900 dark:text-white mb-1 flex items-center gap-1.5">
          <PhoneIncoming className="w-4 h-4 text-blue-500" /> Inbound calls
        </h2>
        <p className="text-[11px] text-gray-500 dark:text-gray-400 mb-3">
          Answer people who dial the Twilio number. Known callers are picked up at their current
          enquiry stage; first-time callers are captured as a new lead. Calling hours above
          do not apply to inbound calls.
        </p>
        <label className="flex items-center gap-2 text-sm cursor-pointer mb-3">
          <input type="checkbox" checked={!!config.inbound_enabled}
            onChange={e => set('inbound_enabled', e.target.checked)} />
          <span className={cn('font-medium', config.inbound_enabled ? 'text-green-600 dark:text-green-400' : 'text-gray-500')}>
            {config.inbound_enabled ? 'Answering inbound calls' : 'Not answering inbound calls'}
          </span>
        </label>
        <div className="grid grid-cols-2 gap-3">
          <div>
            <label className="text-[11px] uppercase text-gray-500 font-medium">New caller becomes</label>
            <select value={config.inbound_lead_target || 'engage'}
              disabled={!config.inbound_enabled}
              onChange={e => set('inbound_lead_target', e.target.value)}
              className="mt-1 w-full px-3 py-2 text-sm rounded-lg bg-gray-50 dark:bg-gray-900/40 border border-gray-200 dark:border-gray-700 text-gray-900 dark:text-white disabled:opacity-50">
              <option value="engage">Engage enquiry</option>
              <option value="trustedge">Gro Wing CRM customer</option>
            </select>
          </div>
        </div>
        <div className="mt-3">
          <label className="text-[11px] uppercase text-gray-500 font-medium">Inbound greeting (optional)</label>
          <textarea value={config.inbound_greeting_template || ''}
            disabled={!config.inbound_enabled}
            onChange={e => set('inbound_greeting_template', e.target.value)}
            placeholder="Blank uses a language-appropriate default. Placeholders: {customer_name}, {company_name}, {agent_name}"
            rows={2}
            className="mt-1 w-full px-3 py-2 text-sm rounded-lg bg-gray-50 dark:bg-gray-900/40 border border-gray-200 dark:border-gray-700 text-gray-900 dark:text-white disabled:opacity-50" />
        </div>
      </div>
    </div>
  );
}


function Input({ label, value, onChange, type = 'text', icon: Icon }) {
  return (
    <div>
      <label className="text-[11px] uppercase text-gray-500 font-medium">{label}</label>
      <div className="relative mt-1">
        {Icon && <Icon className="w-3.5 h-3.5 absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />}
        <input type={type} value={value ?? ''} onChange={e => onChange(e.target.value)}
          className={cn('w-full px-3 py-2 text-sm rounded-lg bg-gray-50 dark:bg-gray-900/40 border border-gray-200 dark:border-gray-700 text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-teal-500',
            Icon ? 'pl-9' : '')} />
      </div>
    </div>
  );
}
